import React from 'react';

interface StyledChordProps {
  chord: string;
  baseFontSize: number;
  className?: string;
  color?: string;
}

const StyledChord: React.FC<StyledChordProps> = ({ chord, baseFontSize, className, color = '#e5e7eb' }) => {
  if (chord === '%') {
    // Make SVG size responsive to baseFontSize - much smaller for repeat symbols
    const svgSize = Math.max(baseFontSize * 0.8, 12); // Minimum 12px, max based on font size
    return (
      <div className="w-full h-full flex items-center justify-center">
        <svg 
          viewBox="0 0 100 100" 
          style={{ 
            width: `${svgSize}px`, 
            height: `${svgSize}px`,
            color
          }}
        >
            <circle cx="30" cy="30" r="10" fill="currentColor" />
            <line x1="15" y1="85" x2="85" y2="15" strokeWidth="10" strokeLinecap="round" stroke="currentColor" />
            <circle cx="70" cy="70" r="10" fill="currentColor" />
        </svg>
      </div>
    );
  }

  // --- PARSING ---
  let mainChordPart = chord;
  let slashBassPart: string | null = null;

  const lastSlashIndex = chord.lastIndexOf('/');
  if (lastSlashIndex > 0 && !chord.includes('6/9')) {
    const potentialBass = chord.substring(lastSlashIndex + 1);
    if (potentialBass.match(/^[A-G][#b♯♭]?.*$/)) {
        mainChordPart = chord.substring(0, lastSlashIndex);
        slashBassPart = `/${potentialBass}`;
    }
  }
  
  const rootRegex = /^([A-G])([#b♯♭]?)(.*)/;
  const match = mainChordPart.match(rootRegex);

  if (!match) {
    return <span className={`leading-none ${className}`} style={{ fontSize: `${baseFontSize}px`, fontFamily: '"DM Serif Text", serif', color }}>{chord}</span>;
  }

  const [, root, accidental, remainder] = match;

  let qualityBase = '';
  let qualitySup = '';

  // New parsing logic to separate baseline qualities (m, maj) from superscript extensions.
  if (remainder.startsWith('maj')) {
    qualityBase = 'maj';
    qualitySup = remainder.substring(3);
  } else if (remainder.startsWith('m')) {
    qualityBase = 'm';
    qualitySup = remainder.substring(1);
  } else {
    // For dominant, diminished, etc., the whole remainder is superscript.
    qualitySup = remainder;
  }

  const extensionFontSize = baseFontSize * 0.6;
  const accidentalFontSize = baseFontSize * 0.65;
  const slashBassFontSize = baseFontSize * 0.55;

  return (
    <div className={`leading-none inline-flex items-baseline ${className}`} style={{ lineHeight: 1, fontFamily: '"DM Serif Text", serif', color }}>
        
        {/* Container for Root, Accidental, and Slash Bass. This establishes the main baseline. */}
        <div className="relative">
            {/* Root and Accidental */}
            <div className="inline-flex items-start">
                <span style={{ fontSize: `${baseFontSize}px` }}>{root}</span>
                {accidental && (
                    <span style={{ 
                        fontSize: `${accidentalFontSize}px`, 
                        marginLeft: `-${baseFontSize * 0.12}px`,
                        marginTop: `${baseFontSize * 0.02}px`,
                    }}>
                        {accidental}
                    </span>
                )}
            </div>

            {/* Slash Bass is positioned absolutely relative to the container above */}
            {slashBassPart && (
              <span style={{
                  position: 'absolute',
                  bottom: `-${baseFontSize * 0.45}px`, 
                  left: '60%',
                  fontSize: `${slashBassFontSize}px`,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
              }}>
                  {slashBassPart}
              </span>
            )}
        </div>
        
        {/* Extensions container (Quality Base + Superscript) */}
        {(qualityBase || qualitySup) && (
            <div 
              className="inline-flex items-baseline" 
              style={{ 
                marginLeft: `${baseFontSize * 0.02}px`,
                lineHeight: 1.1,
              }}
            >
                {qualityBase && (
                    <span style={{ fontSize: `${extensionFontSize}px`, marginRight: '1px' }}>
                        {qualityBase}
                    </span>
                )}
                {qualitySup && (
                    <sup style={{ fontSize: `${extensionFontSize}px` }}>
                        {qualitySup}
                    </sup>
                )}
            </div>
        )}
    </div>
  );
};

export default StyledChord;